import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

const BASE =
  'inline-flex shrink-0 select-none items-center justify-center rounded-md border font-medium ' +
  'leading-none transition-colors duration-150 motion-reduce:transition-none';

const VARIANT_REST: Record<ButtonVariant, string> = {
  primary: 'border-transparent bg-accent text-accent-ink shadow-panel',
  secondary: 'border-border bg-surface text-ink',
  ghost: 'border-transparent bg-transparent text-ink-muted',
  danger: 'border-transparent bg-danger-soft text-danger',
};

const VARIANT_HOVER: Record<ButtonVariant, string> = {
  primary: 'hover:bg-accent-hover',
  secondary: 'hover:bg-surface-sunken hover:border-border-strong',
  ghost: 'hover:bg-surface-sunken hover:text-ink',
  danger: 'hover:bg-danger hover:text-canvas',
};

// Touch targets grow below the `md` breakpoint; pointer-fine desktops keep the
// denser tool-like rhythm the app is designed around.
const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 max-md:h-10 gap-1.5 px-2.5 text-[13px]',
  md: 'h-9 max-md:h-11 gap-2 px-3.5 text-sm',
};

export interface ButtonClassOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
}

export function buttonClassName(options: ButtonClassOptions = {}): string {
  const { variant = 'secondary', size = 'md', fullWidth = false, disabled = false } = options;
  return cn(
    BASE,
    VARIANT_REST[variant],
    !disabled && VARIANT_HOVER[variant],
    SIZE[size],
    fullWidth ? 'w-full' : '',
    disabled && 'cursor-not-allowed opacity-55 shadow-none',
    options.className,
  );
}

export interface IconButtonClassOptions {
  variant?: ButtonVariant;
  /** Allows the 44px target to shrink to 36px on precise pointers only. */
  compact?: boolean;
  pressed?: boolean;
  disabled?: boolean;
  className?: string;
}

const COMPACT =
  '[@media(pointer:fine)]:h-9 [@media(pointer:fine)]:w-9 ' +
  '[@media(pointer:fine)]:min-h-9 [@media(pointer:fine)]:min-w-9';

export function iconButtonClassName(options: IconButtonClassOptions = {}): string {
  const { variant = 'ghost', compact = false, pressed = false, disabled = false } = options;
  return cn(
    BASE,
    'h-11 w-11 min-h-11 min-w-11 p-0',
    // `fs-touch` is authored as plain CSS, so it cannot carry variants — it is
    // only safe to apply when nothing needs to shrink the target later.
    !compact && 'fs-touch',
    compact && COMPACT,
    VARIANT_REST[variant],
    !disabled && VARIANT_HOVER[variant],
    pressed && 'bg-accent-soft text-accent border-transparent',
    disabled && 'cursor-not-allowed opacity-55 shadow-none',
    options.className,
  );
}
